/** The only page sizes the footer control offers. */
export const PAGE_SIZES = [5, 10, 25, 50];
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Clamps a raw `pageSize` query param to the sizes the footer control offers.
 * Every list that reads the param goes through here — otherwise the footer shows
 * a clamped value while the request still carries the raw one, and a hand-edited
 * `?pageSize=100000` forces an unbounded fetch.
 *
 * Lives in its own module so consumers do not have to import the component (and
 * so mocking that component in a test cannot hide the helper).
 */
export const clampPageSize = (
  raw: string | null,
  fallback = DEFAULT_PAGE_SIZE,
) => (PAGE_SIZES.includes(Number(raw)) ? Number(raw) : fallback);

/**
 * Clamps a raw `page` query param to a safe positive integer. The lists turn it
 * into a zero-based request offset, so `?page=-1` would send `-2` and `?page=1.5`
 * a fractional page — and the "page beyond the last one" correction only
 * triggers when the value is too high, never when it is negative.
 *
 * `Number.isSafeInteger` also rejects `Infinity` (`?page=Infinity`, `?page=1e309`)
 * and anything past 2^53-1, which would otherwise reach the request verbatim.
 */
export const clampPage = (raw: string | null) => {
  const page = Math.trunc(Number(raw));

  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
};
